/**
 * A small Markdown parser for the assistant's answers. It only builds a tree of plain data; the
 * AiMarkdown component turns that tree into React elements, so nothing the model writes is ever
 * handed to the browser as HTML: raw HTML stays text, and a link survives only when it points to
 * http(s) or to a page of the app ("/...").
 *
 * Supported: paragraphs with line breaks, # headings, **bold**, *italic*, ~~strikethrough~~,
 * `code`, fenced code blocks, ordered and unordered lists (nested by indentation), > quotes,
 * GFM tables, horizontal rules, [links](url) and bare http(s) URLs. Anything else is shown as
 * the text it is. The parser also has to cope with half-written Markdown, because it re-runs on
 * every streamed chunk: an unclosed fence or ** simply renders as code or as the literal stars.
 */

export type AiInlineNode =
  | { type: "text"; text: string }
  | { type: "strong"; children: AiInlineNode[] }
  | { type: "em"; children: AiInlineNode[] }
  | { type: "del"; children: AiInlineNode[] }
  | { type: "code"; text: string }
  | { type: "link"; href: string; external: boolean; children: AiInlineNode[] }
  | { type: "br" };

export interface IAiListItem {
  content: AiInlineNode[];
  children: IAiListBlock[];
}

export interface IAiListBlock {
  type: "list";
  ordered: boolean;
  start: number;
  items: IAiListItem[];
}

export type AiTableAlign = "left" | "center" | "right" | null;

export type AiBlockNode =
  | { type: "paragraph"; content: AiInlineNode[] }
  | { type: "heading"; level: 3 | 4 | 5; content: AiInlineNode[] }
  | { type: "code"; language: string | null; code: string }
  | IAiListBlock
  | { type: "quote"; children: AiBlockNode[] }
  | { type: "table"; align: AiTableAlign[]; header: AiInlineNode[][]; rows: AiInlineNode[][][] }
  | { type: "hr" };

const MAX_INLINE_DEPTH = 6;
const MAX_QUOTE_DEPTH = 4;
const MAX_LIST_DEPTH = 8;
/** How far a "[" looks for its "](...)"; keeps a line full of brackets linear enough. */
const MAX_LINK_SCAN = 2000;

const FENCE_OPEN = /^(\s*)(`{3,}|~{3,})(.*)$/;
const FENCE_CLOSE = /^\s*(`{3,}|~{3,})\s*$/;
// No lazy or optional groups next to \s* here: on a long line of spaces they backtrack in quadratic time.
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/;
const HORIZONTAL_RULE = /^\s{0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])(?:[ \t]+(.*))?$/;
const QUOTE = /^\s{0,3}>[ \t]?(.*)$/;
const TABLE_SEPARATOR = /^\s*(?:\|\s*)?:?-+:?\s*(?:\|\s*:?-+:?\s*)*(?:\|\s*)?$/;
const ESCAPABLE = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;
const BARE_URL = /https?:\/\/[^\s<>"'`]+/iy;
const ANGLE_URL = /<(https?:\/\/[^\s<>]+)>/iy;

const isBlank = (line: string) => line.trim() === "";

/** "Заглавие ##" → "Заглавие": a closing run of # after a space goes; "C#" keeps its hash. */
const stripClosingHashes = (text: string): string => {
  const trimmed = text.trimEnd();
  let end = trimmed.length;
  while (end > 0 && trimmed[end - 1] === "#") end -= 1;
  if (end === trimmed.length || end === 0 || !/\s/.test(trimmed[end - 1])) return trimmed;
  return trimmed.slice(0, end).trimEnd();
};

const isWhitespace = (ch: string | undefined) => ch === undefined || /\s/.test(ch);

const isAlphanumeric = (ch: string | undefined) => ch !== undefined && /[\p{L}\p{N}]/u.test(ch);

const indentOf = (line: string): number => {
  let width = 0;
  for (const ch of line) {
    if (ch === " ") width += 1;
    else if (ch === "\t") width += 4;
    else break;
  }
  return width;
};

const countRun = (text: string, from: number, ch: string): number => {
  let end = from;
  while (text[end] === ch) end += 1;
  return end - from;
};

/** Index of a run of exactly `length` backticks at or after `from`, or -1. */
const findClosingBackticks = (text: string, from: number, length: number): number => {
  let index = text.indexOf("`", from);
  while (index !== -1) {
    const run = countRun(text, index, "`");
    if (run === length) return index;
    index = text.indexOf("`", index + run);
  }
  return -1;
};

/**
 * The http(s) or app-relative target of a link, or null when it must not become one
 * (javascript:, data:, protocol-relative "//host", anything with spaces or control characters).
 */
export const getSafeHref = (raw: string): { href: string; external: boolean } | null => {
  const url = raw.trim();
  // eslint-disable-next-line no-control-regex
  if (!url || /[\u0000- \u007f]/.test(url)) return null;

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:" ? { href: url, external: true } : null;
    } catch {
      return null;
    }
  }

  if (url.startsWith("/") && !url.startsWith("//") && !url.startsWith("/\\")) {
    return { href: url, external: false };
  }

  return null;
};

/** Drops trailing punctuation that belongs to the sentence, not to the URL ("виж https://x.bg."). */
const trimUrlEnd = (url: string): string => {
  let end = url.length;
  for (;;) {
    const last = url[end - 1];
    if (!last) break;
    if (".,;:!?\"'»".includes(last)) {
      end -= 1;
      continue;
    }
    if (last === ")") {
      const slice = url.slice(0, end);
      const opens = slice.split("(").length - 1;
      const closes = slice.split(")").length - 1;
      if (closes > opens) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return url.slice(0, end);
};

// ---- Inline ----

/**
 * Emphasis follows CommonMark's delimiter-run algorithm: the text is scanned once into a list of
 * nodes and "*"/"_" runs, then every closing run is matched with the nearest opening run before
 * it. Scanning forward for a closer from every opener instead (and recursing into each match)
 * grows exponentially on input like "*a **b _c" repeated, and the parser re-runs on every
 * streamed chunk, so that would freeze the page.
 */
interface IDelimiterRun {
  ch: "*" | "_";
  /** Delimiters of the run not used up yet. */
  count: number;
  /** The run's length as written; the "rule of 3" looks at it. */
  original: number;
  canOpen: boolean;
  canClose: boolean;
}

interface IInlineSlot {
  node: AiInlineNode | null;
  run: IDelimiterRun | null;
  prev: IInlineSlot | null;
  next: IInlineSlot | null;
}

const isPunctuation = (ch: string | undefined) => ch !== undefined && /[\p{P}\p{S}]/u.test(ch);

/** Can the run of `ch` between `before` and `after` open and/or close emphasis? */
const describeRun = (ch: "*" | "_", length: number, before: string | undefined, after: string | undefined): IDelimiterRun => {
  const leftFlanking =
    !isWhitespace(after) && (!isPunctuation(after) || isWhitespace(before) || isPunctuation(before));
  const rightFlanking =
    !isWhitespace(before) && (!isPunctuation(before) || isWhitespace(after) || isPunctuation(after));
  // "_" never opens or closes inside a word, so snake_case and file_names stay as they are.
  const canOpen = ch === "*" ? leftFlanking : leftFlanking && (!rightFlanking || isPunctuation(before));
  const canClose = ch === "*" ? rightFlanking : rightFlanking && (!leftFlanking || isPunctuation(after));
  return { ch, count: length, original: length, canOpen, canClose };
};

const pushText = (nodes: AiInlineNode[], text: string) => {
  if (!text) return;
  const last = nodes[nodes.length - 1];
  if (last && last.type === "text") {
    last.text += text;
  } else {
    nodes.push({ type: "text", text });
  }
};

const pushNode = (nodes: AiInlineNode[], node: AiInlineNode) => {
  if (node.type === "text") pushText(nodes, node.text);
  else nodes.push(node);
};

/** The nodes from `from` up to (not including) `to`; runs still left over are plain text. */
const collectSlots = (from: IInlineSlot | null, to: IInlineSlot | null): AiInlineNode[] => {
  const nodes: AiInlineNode[] = [];
  for (let slot = from; slot && slot !== to; slot = slot.next) {
    if (slot.node) pushNode(nodes, slot.node);
    else if (slot.run && slot.run.count > 0) pushText(nodes, slot.run.ch.repeat(slot.run.count));
  }
  return nodes;
};

const unlink = (slot: IInlineSlot) => {
  if (slot.prev) slot.prev.next = slot.next;
  if (slot.next) slot.next.prev = slot.prev;
};

/** Matches the runs after `head` into em/strong nodes, in place. */
const processEmphasis = (head: IInlineSlot) => {
  // Per kind of closer: the slot below which no opener for it exists (CommonMark's openers_bottom).
  const openersBottom = new Map<string, IInlineSlot | null>();
  let closer = head.next;

  while (closer) {
    const closing = closer.run;
    if (!closing || !closing.canClose) {
      closer = closer.next;
      continue;
    }

    const bottomKey = `${closing.ch}${closing.canOpen ? 1 : 0}${closing.original % 3}`;
    const bottom = openersBottom.get(bottomKey) ?? null;
    let opener: IInlineSlot | null = null;
    for (let slot = closer.prev; slot && slot !== bottom && slot !== head; slot = slot.prev) {
      const candidate = slot.run;
      if (!candidate || candidate.ch !== closing.ch || !candidate.canOpen) continue;
      // The rule of 3: in "*a**b*" the "**" cannot close the first "*".
      const breaksRuleOfThree =
        (candidate.canClose || closing.canOpen) &&
        (candidate.original + closing.original) % 3 === 0 &&
        !(candidate.original % 3 === 0 && closing.original % 3 === 0);
      if (!breaksRuleOfThree) {
        opener = slot;
        break;
      }
    }

    if (!opener) {
      openersBottom.set(bottomKey, closer.prev);
      if (!closing.canOpen) {
        // It can never be used now: plain text from here on.
        closer.node = { type: "text", text: closing.ch.repeat(closing.count) };
        closer.run = null;
      }
      closer = closer.next;
      continue;
    }

    const opening = opener.run!;
    const used = opening.count >= 2 && closing.count >= 2 ? 2 : 1;
    const children = collectSlots(opener.next, closer);
    const wrapped: IInlineSlot = {
      node: used === 2 ? { type: "strong", children } : { type: "em", children },
      run: null,
      prev: opener,
      next: closer,
    };
    opener.next = wrapped;
    closer.prev = wrapped;

    opening.count -= used;
    closing.count -= used;
    if (opening.count === 0) unlink(opener);
    if (closing.count === 0) {
      const next = closer.next;
      unlink(closer);
      closer = next;
    }
    // Otherwise the same closer goes on with its remaining delimiters ("***a**b*").
  }
};

/** `[label](url)` starting at `start` ("["); null when it is not a complete link. */
const tryLink = (
  text: string,
  start: number,
  depth: number,
  isImage: boolean,
): { nodes: AiInlineNode[]; end: number } | null => {
  let level = 0;
  let labelEnd = -1;
  const scanLimit = Math.min(text.length, start + MAX_LINK_SCAN);
  for (let index = start; index < scanLimit; index += 1) {
    const ch = text[index];
    if (ch === "\\") {
      index += 1;
      continue;
    }
    if (ch === "[") level += 1;
    if (ch === "]") {
      level -= 1;
      if (level === 0) {
        labelEnd = index;
        break;
      }
    }
  }
  if (labelEnd === -1 || text[labelEnd + 1] !== "(") return null;

  let parens = 0;
  let targetEnd = -1;
  for (let index = labelEnd + 1; index < scanLimit; index += 1) {
    const ch = text[index];
    if (ch === "\\") {
      index += 1;
      continue;
    }
    if (ch === "(") parens += 1;
    if (ch === ")") {
      parens -= 1;
      if (parens === 0) {
        targetEnd = index;
        break;
      }
    }
  }
  if (targetEnd === -1) return null;

  const label = text.slice(start + 1, labelEnd);
  let target = text.slice(labelEnd + 2, targetEnd).trim();
  if (target.startsWith("<")) {
    const close = target.indexOf(">");
    target = close === -1 ? target : target.slice(1, close);
  } else {
    // [text](url "title"): the title is dropped.
    target = target.split(/\s+/)[0] ?? "";
  }

  const labelNodes: AiInlineNode[] = label.trim()
    ? parseInline(label, depth + 1)
    : [{ type: "text", text: isImage ? "изображение" : target }];
  const safe = getSafeHref(target);
  // Images are never loaded from the model's answer; an image becomes a link to it.
  const nodes: AiInlineNode[] = safe
    ? [{ type: "link", href: safe.href, external: safe.external, children: labelNodes }]
    : labelNodes;
  return { nodes, end: targetEnd + 1 };
};

export const parseInline = (text: string, depth = 0): AiInlineNode[] => {
  const head: IInlineSlot = { node: null, run: null, prev: null, next: null };
  let tail = head;
  // Consecutive characters are collected here and become one text slot.
  let pendingText = "";

  const link = (node: AiInlineNode | null, run: IDelimiterRun | null) => {
    const added: IInlineSlot = { node, run, prev: tail, next: null };
    tail.next = added;
    tail = added;
  };
  const flushText = () => {
    if (!pendingText) return;
    link({ type: "text", text: pendingText }, null);
    pendingText = "";
  };
  const appendText = (value: string) => {
    pendingText += value;
  };
  const appendNode = (node: AiInlineNode) => {
    if (node.type === "text") {
      appendText(node.text);
      return;
    }
    flushText();
    link(node, null);
  };
  const appendRun = (run: IDelimiterRun) => {
    flushText();
    link(null, run);
  };

  let index = 0;
  while (index < text.length) {
    const ch = text[index];

    if (ch === "\\" && index + 1 < text.length && ESCAPABLE.test(text[index + 1])) {
      appendText(text[index + 1]);
      index += 2;
      continue;
    }

    if (ch === "`") {
      const run = countRun(text, index, "`");
      const close = findClosingBackticks(text, index + run, run);
      if (close !== -1) {
        let code = text.slice(index + run, close);
        if (code.length >= 2 && code.startsWith(" ") && code.endsWith(" ") && code.trim()) {
          code = code.slice(1, -1);
        }
        appendNode({ type: "code", text: code });
        index = close + run;
      } else {
        appendText(text.slice(index, index + run));
        index += run;
      }
      continue;
    }

    if (ch === "*" || ch === "_") {
      const run = countRun(text, index, ch);
      const delimiter = describeRun(ch, run, text[index - 1], text[index + run]);
      if (delimiter.canOpen || delimiter.canClose) appendRun(delimiter);
      else appendText(text.slice(index, index + run));
      index += run;
      continue;
    }

    if (ch === "~" && text[index + 1] === "~" && depth < MAX_INLINE_DEPTH) {
      const close = text.indexOf("~~", index + 2);
      if (close > index + 2 && !isWhitespace(text[index + 2]) && !isWhitespace(text[close - 1])) {
        appendNode({ type: "del", children: parseInline(text.slice(index + 2, close), depth + 1) });
        index = close + 2;
        continue;
      }
    }

    if ((ch === "[" || (ch === "!" && text[index + 1] === "[")) && depth < MAX_INLINE_DEPTH) {
      const isImage = ch === "!";
      const link = tryLink(text, isImage ? index + 1 : index, depth, isImage);
      if (link) {
        link.nodes.forEach(appendNode);
        index = link.end;
        continue;
      }
    }

    if (ch === "<") {
      ANGLE_URL.lastIndex = index;
      const match = ANGLE_URL.exec(text);
      const safe = match ? getSafeHref(match[1]) : null;
      if (match && safe) {
        appendNode({ type: "link", href: safe.href, external: true, children: [{ type: "text", text: match[1] }] });
        index += match[0].length;
        continue;
      }
    }

    if ((ch === "h" || ch === "H") && !isAlphanumeric(text[index - 1])) {
      BARE_URL.lastIndex = index;
      const match = BARE_URL.exec(text);
      if (match) {
        const url = trimUrlEnd(match[0]);
        const safe = getSafeHref(url);
        if (safe) {
          appendNode({ type: "link", href: safe.href, external: true, children: [{ type: "text", text: url }] });
          index += url.length;
          continue;
        }
      }
    }

    appendText(ch);
    index += 1;
  }

  flushText();
  processEmphasis(head);
  return collectSlots(head.next, null);
};

/** Lines of one paragraph or list item, joined with line breaks. */
const parseInlineLines = (lines: string[]): AiInlineNode[] => {
  const nodes: AiInlineNode[] = [];
  lines.forEach((line, index) => {
    if (index > 0) nodes.push({ type: "br" });
    nodes.push(...parseInline(line.trim()));
  });
  return nodes;
};

// ---- Blocks ----

interface IListEntry {
  indent: number;
  ordered: boolean;
  number: number;
  lines: string[];
}

const matchListItem = (line: string): IListEntry | null => {
  if (HORIZONTAL_RULE.test(line)) return null;
  const match = LIST_ITEM.exec(line);
  if (!match) return null;
  const marker = match[2];
  const ordered = /\d/.test(marker);
  return {
    indent: indentOf(match[1]),
    ordered,
    number: ordered ? parseInt(marker, 10) : 1,
    lines: [match[3] ?? ""],
  };
};

const splitTableRow = (line: string): string[] => {
  let row = line.trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|") && !row.endsWith("\\|")) row = row.slice(0, -1);

  const cells: string[] = [];
  let current = "";
  let inCode = false;
  for (let index = 0; index < row.length; index += 1) {
    const ch = row[index];
    if (ch === "\\" && row[index + 1] === "|") {
      current += "|";
      index += 1;
      continue;
    }
    if (ch === "`") inCode = !inCode;
    if (ch === "|" && !inCode) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const isTableStart = (lines: string[], index: number): boolean => {
  const header = lines[index];
  const separator = lines[index + 1];
  if (!header || separator === undefined || !header.includes("|")) return false;
  if (!separator.includes("|") || !separator.includes("-") || !TABLE_SEPARATOR.test(separator)) return false;
  return splitTableRow(header).length === splitTableRow(separator).length;
};

const parseAlign = (cell: string): AiTableAlign => {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return null;
};

/** Does this line start a block that ends a paragraph or a list? */
const startsOtherBlock = (lines: string[], index: number): boolean => {
  const line = lines[index];
  return (
    FENCE_OPEN.test(line) ||
    HEADING.test(line) ||
    HORIZONTAL_RULE.test(line) ||
    QUOTE.test(line) ||
    isTableStart(lines, index)
  );
};

const buildListTree = (entries: IListEntry[]): IAiListBlock => {
  const root: IAiListBlock = { type: "list", ordered: entries[0].ordered, start: entries[0].number, items: [] };
  const stack: { indent: number; list: IAiListBlock }[] = [{ indent: entries[0].indent, list: root }];

  entries.forEach((entry) => {
    let top = stack[stack.length - 1];
    while (stack.length > 1 && entry.indent < top.indent) {
      stack.pop();
      top = stack[stack.length - 1];
    }
    if (entry.indent >= top.indent + 2 && top.list.items.length > 0 && stack.length < MAX_LIST_DEPTH) {
      const parent = top.list.items[top.list.items.length - 1];
      const child: IAiListBlock = { type: "list", ordered: entry.ordered, start: entry.number, items: [] };
      parent.children.push(child);
      stack.push({ indent: entry.indent, list: child });
      top = stack[stack.length - 1];
    }
    top.list.items.push({ content: parseInlineLines(entry.lines), children: [] });
  });

  return root;
};

const parseList = (lines: string[], start: number): { block: IAiListBlock; next: number } => {
  const entries: IListEntry[] = [];
  let index = start;

  while (index < lines.length) {
    const line = lines[index];
    const item = matchListItem(line);
    if (item) {
      // "1." after "-" (or the other way round) at the top level starts a new list.
      const first = entries[0];
      if (first && item.indent < first.indent + 2 && item.ordered !== first.ordered) break;
      entries.push(item);
      index += 1;
      continue;
    }

    if (isBlank(line)) {
      // A blank line keeps the list going only when more of the list follows it.
      let next = index + 1;
      while (next < lines.length && isBlank(lines[next])) next += 1;
      const continues =
        next < lines.length &&
        (!!matchListItem(lines[next]) || (indentOf(lines[next]) >= 2 && !startsOtherBlock(lines, next)));
      if (!continues) break;
      index = next;
      continue;
    }

    if (startsOtherBlock(lines, index)) break;
    // A plain line right under an item continues that item.
    entries[entries.length - 1].lines.push(line.trim());
    index += 1;
  }

  return { block: buildListTree(entries), next: index };
};

const parseBlocks = (lines: string[], quoteDepth: number): AiBlockNode[] => {
  const blocks: AiBlockNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (isBlank(line)) {
      index += 1;
      continue;
    }

    const fence = FENCE_OPEN.exec(line);
    if (fence && !(fence[2][0] === "`" && fence[3].includes("`"))) {
      const indent = indentOf(fence[1]);
      const marker = fence[2];
      const language = /^[\w+#.-]+/.exec(fence[3].trim())?.[0] ?? null;
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length) {
        const closing = FENCE_CLOSE.exec(lines[index]);
        if (closing && closing[1][0] === marker[0] && closing[1].length >= marker.length) {
          index += 1;
          break;
        }
        // Code indented under a list item loses that indentation.
        codeLines.push(lines[index].slice(Math.min(indent, indentOf(lines[index]))));
        index += 1;
      }
      blocks.push({ type: "code", language, code: codeLines.join("\n") });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      const hashes = heading[1].length;
      const level: 3 | 4 | 5 = hashes <= 2 ? 3 : hashes === 3 ? 4 : 5;
      blocks.push({ type: "heading", level, content: parseInline(stripClosingHashes(heading[2])) });
      index += 1;
      continue;
    }

    if (HORIZONTAL_RULE.test(line)) {
      blocks.push({ type: "hr" });
      index += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const quoteLines: string[] = [];
      while (index < lines.length && QUOTE.test(lines[index])) {
        quoteLines.push(QUOTE.exec(lines[index])![1]);
        index += 1;
      }
      if (quoteDepth < MAX_QUOTE_DEPTH) {
        blocks.push({ type: "quote", children: parseBlocks(quoteLines, quoteDepth + 1) });
      } else {
        blocks.push({ type: "paragraph", content: parseInlineLines(quoteLines) });
      }
      continue;
    }

    if (isTableStart(lines, index)) {
      const header = splitTableRow(line);
      const align = splitTableRow(lines[index + 1]).map(parseAlign);
      const rows: AiInlineNode[][][] = [];
      index += 2;
      while (index < lines.length && !isBlank(lines[index]) && lines[index].includes("|")) {
        const cells = splitTableRow(lines[index]);
        rows.push(header.map((_, column) => parseInline(cells[column] ?? "")));
        index += 1;
      }
      blocks.push({ type: "table", align, header: header.map((cell) => parseInline(cell)), rows });
      continue;
    }

    if (matchListItem(line)) {
      const { block, next } = parseList(lines, index);
      blocks.push(block);
      index = next;
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length && !isBlank(lines[index])) {
      if (paragraph.length > 0 && (startsOtherBlock(lines, index) || matchListItem(lines[index]))) break;
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push({ type: "paragraph", content: parseInlineLines(paragraph) });
  }

  return blocks;
};

export const parseAiMarkdown = (markdown: string): AiBlockNode[] => {
  if (!markdown) return [];
  return parseBlocks(markdown.replace(/\r\n?/g, "\n").split("\n"), 0);
};
